import { CalendarTimeEntry } from '../../services/types';

// Company color palette
export const COMPANY_COLORS = [
  { bg: 'bg-blue-500', hex: '#3b82f6' },
  { bg: 'bg-emerald-500', hex: '#10b981' },
  { bg: 'bg-amber-500', hex: '#f59e0b' },
  { bg: 'bg-cyan-500', hex: '#06b6d4' },
  { bg: 'bg-rose-500', hex: '#f43f5e' },
  { bg: 'bg-teal-500', hex: '#14b8a6' },
  { bg: 'bg-orange-500', hex: '#f97316' },
  { bg: 'bg-indigo-500', hex: '#6366f1' },
  { bg: 'bg-purple-500', hex: '#a855f7' },
  { bg: 'bg-pink-500', hex: '#ec4899' },
  { bg: 'bg-lime-500', hex: '#84cc16' },
  { bg: 'bg-red-500', hex: '#ef4444' },
];

export const shortDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
export const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Normalize company name for consistent matching
 */
export function normalizeCompanyName(name: string | undefined | null): string {
  if (!name) return '';
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[,\s]+Inc\.?$/i, ', Inc.')
    .replace(/[,\s]+LLC\.?$/i, ', LLC')
    .replace(/[,\s]+Ltd\.?$/i, ', Ltd.')
    .replace(/\.$/g, '');
}

/**
 * Get consistent color for a company name
 */
export function getCompanyColor(companyName: string): { bg: string; hex: string } {
  const normalized = normalizeCompanyName(companyName);
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = ((hash << 5) - hash) + normalized.charCodeAt(i);
    hash = hash & hash;
  }
  return COMPANY_COLORS[Math.abs(hash) % COMPANY_COLORS.length];
}

/**
 * Format hours for display. Safely handles undefined/null (renders "—h")
 * because Cosmos time-entry docs occasionally have no hoursWorked and
 * every caller passing raw entry.hoursWorked without a guard would crash
 * (blank-Calendar via TypeError on toFixed — Jacob 2026-07-24).
 */
export function formatHours(hours: number | undefined | null): string {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) return '—h';
  return hours.toFixed(2) + 'h';
}

/**
 * Format date for display (e.g., "Monday, January 26, 2026")
 */
export function formatDisplayDate(date: Date): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${days[date.getDay()]}, ${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Format short date (e.g., "Mon, Jan 26")
 */
export function formatShortDate(date: Date): string {
  return `${dayNames[date.getDay()]}, ${shortMonthNames[date.getMonth()]} ${date.getDate()}`;
}

/**
 * Format date key for byDate lookup (YYYY-MM-DD)
 */
export function formatDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Get total hours from entries
 */
export function getTotalHours(entries: CalendarTimeEntry[]): number {
  return entries.reduce((sum, e) => sum + e.hoursWorked, 0);
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

/**
 * Get all dates for a week (Sunday-Saturday)
 */
export function getWeekDates(date: Date): Date[] {
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - date.getDay());

  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    dates.push(d);
  }
  return dates;
}

/**
 * Get work week dates (Monday-Friday)
 */
export function getWorkWeekDates(date: Date): Date[] {
  const startOfWeek = new Date(date);
  const dayOfWeek = date.getDay();
  // Go to Monday (1) of current week
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  startOfWeek.setDate(date.getDate() + diff);

  const dates: Date[] = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    dates.push(d);
  }
  return dates;
}

/**
 * Get opacity based on hours (for year view)
 */
export function getOpacityByHours(hours: number): number {
  if (hours >= 8) return 1;
  if (hours >= 6) return 0.8;
  if (hours >= 4) return 0.6;
  if (hours >= 2) return 0.4;
  return 0.2;
}

/**
 * Get opacity based on entry count (for year view)
 */
export function getOpacityByEntries(count: number): number {
  if (count >= 4) return 1;
  if (count >= 3) return 0.75;
  if (count >= 2) return 0.5;
  return 0.25;
}
